import React, { useState, useEffect } from 'react';
import './App.css';
import {
  Phone, MessageSquare, Mail, Zap, Shield, BarChart2, Globe, Users,
  ChevronRight, CheckCircle, ArrowRight, Building2, Briefcase, Star,
  HeadphonesIcon, Brain, Layers, Menu, X
} from 'lucide-react';

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = ['Features', 'Solutions', 'Industries', 'Contact'];

  return (
    <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
      <div className="nav__inner">
        <div className="nav__logo">
          <span className="nav__logo-mark">S</span>
          <span className="nav__logo-text">Saral<strong>Contact</strong></span>
        </div>
        <ul className={`nav__links ${menuOpen ? 'nav__links--open' : ''}`}>
          {links.map(l => (
            <li key={l}><a href={`#${l.toLowerCase()}`} onClick={() => setMenuOpen(false)}>{l}</a></li>
          ))}
          <li><a href="#contact" className="nav__cta" onClick={() => setMenuOpen(false)}>Get Started</a></li>
        </ul>
        <button className="nav__burger" onClick={() => setMenuOpen(!menuOpen)} aria-label="menu">
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </nav>
  );
};

const Hero = () => (
  <section className="hero" id="home">
    <div className="hero__bg">
      <div className="hero__grid" />
      <div className="hero__glow hero__glow--1" />
      <div className="hero__glow hero__glow--2" />
    </div>
    <div className="container hero__content">
      <div className="hero__badge">
        <Zap size={14} /> Powered by Amazon Connect &amp; AWS AI
      </div>
      <h1 className="hero__title">
        AI-Powered Contact Center<br />
        <span className="hero__title-accent">for Every Business</span>
      </h1>
      <p className="hero__sub">
        From a solo AI receptionist to a fully managed enterprise CCaaS—
        start with one use case and scale without limits.
      </p>
      <div className="hero__actions">
        <a href="#contact" className="btn btn--primary">Start Free Consultation <ArrowRight size={16} /></a>
        <a href="#solutions" className="btn btn--ghost">Explore Solutions</a>
      </div>
      <div className="hero__stats">
        {[['24/7', 'AI Coverage'], ['4 Tiers', 'Solopreneur to Enterprise'], ['12+', 'AWS Services']].map(([v, l]) => (
          <div key={l} className="hero__stat">
            <span className="hero__stat-val">{v}</span>
            <span className="hero__stat-label">{l}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Channels = () => {
  const items = [
    { icon: <Phone size={18} />, label: 'Voice' },
    { icon: <MessageSquare size={18} />, label: 'Chat' },
    { icon: <Mail size={18} />, label: 'Email' },
    { icon: <MessageSquare size={18} />, label: 'SMS' },
    { icon: <MessageSquare size={18} />, label: 'WhatsApp' },
    { icon: <Brain size={18} />, label: 'AI Agents' },
    { icon: <BarChart2 size={18} />, label: 'Analytics' },
    { icon: <Shield size={18} />, label: 'Compliance' },
  ];
  return (
    <div className="channels">
      <div className="channels__inner">
        {items.map(({ icon, label }) => (
          <div key={label} className="channels__item">
            {icon}<span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const features = [
  { icon: <Globe size={28} />, title: 'Omnichannel', desc: 'Voice, Chat, SMS, Email, and WhatsApp unified in one intelligent platform.' },
  { icon: <Brain size={28} />, title: 'AI-Powered Agents', desc: 'Amazon Bedrock & Lex-powered virtual agents handle 60%+ of inquiries autonomously.' },
  { icon: <Layers size={28} />, title: 'Intelligent Routing', desc: 'Skills-based, priority, and AI-driven routing ensures customers reach the right agent.' },
  { icon: <BarChart2 size={28} />, title: 'Analytics & Insights', desc: 'Real-time dashboards, CSAT, NPS, sentiment analysis, and AI containment metrics.' },
  { icon: <Shield size={28} />, title: 'Enterprise Security', desc: 'HIPAA, PCI, SOC compliant with encryption, IAM controls, and full audit trails.' },
  { icon: <Building2 size={28} />, title: 'CRM Integrations', desc: 'Native connectors for Salesforce, ServiceNow, SAP, Oracle, and custom APIs.' },
];

const Features = () => (
  <section className="section features" id="features">
    <div className="container">
      <div className="section__header">
        <p className="section__eyebrow">Capabilities</p>
        <h2 className="section__title">Everything a Modern Contact Center Needs</h2>
        <p className="section__sub">Built entirely on AWS managed services—no servers to provision, no infrastructure to maintain.</p>
      </div>
      <div className="features__grid">
        {features.map(f => (
          <div key={f.title} className="feature-card">
            <div className="feature-card__icon">{f.icon}</div>
            <h3 className="feature-card__title">{f.title}</h3>
            <p className="feature-card__desc">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const tiers = [
  {
    level: 'Starter',
    target: 'Solopreneurs & Small Businesses',
    users: '1–5 users',
    color: '#6C63FF',
    icon: <Star size={22} />,
    highlights: ['AI Receptionist (24/7)', 'Business Phone Number', 'Missed Call SMS', 'Appointment Scheduling', 'Lead Capture', 'AI FAQ Bot'],
    cta: 'Start Simple',
    example: 'Perfect for dentists, realtors, law offices, and independent consultants.',
  },
  {
    level: 'Growth',
    target: 'Growing Startups',
    users: '5–50 agents',
    color: '#00C9A7',
    icon: <Briefcase size={22} />,
    highlights: ['Omnichannel Support', 'Queue & Skill Routing', 'AI Sales Assistant', 'CRM Integration', 'Ticket Creation', 'Campaign Hotline'],
    cta: 'Grow Fast',
    example: 'An ecommerce startup—AI handles 60% of queries, 10 agents handle the rest.',
    badge: 'Popular',
  },
  {
    level: 'Professional',
    target: 'Mid-Market',
    users: '50–300 agents',
    color: '#FF6B6B',
    icon: <HeadphonesIcon size={22} />,
    highlights: ['Full Omnichannel + WhatsApp', 'AI Agent + Knowledge Base', 'Supervisor Tools', 'Workforce Optimization', 'Sentiment Analysis', 'CSAT / NPS Reporting'],
    cta: 'Scale Up',
    example: 'Healthcare, retail, and financial services with growing support teams.',
  },
  {
    level: 'Enterprise',
    target: 'Large Organizations',
    users: '300+ agents',
    color: '#FFB347',
    icon: <Building2 size={22} />,
    highlights: ['Fully Managed CCaaS', 'Amazon Bedrock Agents', 'Global Voice', 'Enterprise Integrations', 'HIPAA / PCI / SOC', '24×7 Operations'],
    cta: 'Talk to Sales',
    example: 'Global deployments with disaster recovery, compliance, and dedicated SLAs.',
  },
];

const Solutions = () => (
  <section className="section solutions" id="solutions">
    <div className="container">
      <div className="section__header">
        <p className="section__eyebrow">Service Tiers</p>
        <h2 className="section__title">Start Small. Scale Big.</h2>
        <p className="section__sub">Land with one use case. Expand without replatforming.</p>
      </div>
      <div className="tiers__grid">
        {tiers.map(t => (
          <div key={t.level} className="tier-card" style={{ '--tier-color': t.color } as React.CSSProperties}>
            {t.badge && <div className="tier-card__badge">{t.badge}</div>}
            <div className="tier-card__header">
              <div className="tier-card__icon">{t.icon}</div>
              <div>
                <h3 className="tier-card__level">{t.level}</h3>
                <p className="tier-card__target">{t.target}</p>
                <span className="tier-card__users">{t.users}</span>
              </div>
            </div>
            <ul className="tier-card__list">
              {t.highlights.map(h => (
                <li key={h}><CheckCircle size={15} />{h}</li>
              ))}
            </ul>
            <p className="tier-card__example">{t.example}</p>
            <a href="#contact" className="tier-card__cta">{t.cta} <ChevronRight size={15} /></a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const deployments = [
  { num: '01', title: 'AI Receptionist', desc: 'One phone number → AI answers every call → transfer to owner if needed.', icon: <Phone size={24} /> },
  { num: '02', title: 'Help Desk', desc: 'Phone + Chat + Email → smart queue → agents → CRM ticket created automatically.', icon: <HeadphonesIcon size={24} /> },
  { num: '03', title: 'AI + Human Hybrid', desc: 'All channels → AI agents resolve 60%+ → knowledge base → human agents → CRM.', icon: <Brain size={24} /> },
  { num: '04', title: 'Managed Enterprise CCaaS', desc: 'Global customers → omnichannel AI platform → Amazon Connect → enterprise integrations → analytics → fully managed by Saral Contact.', icon: <Globe size={24} /> },
];

const HowItWorks = () => (
  <section className="section how">
    <div className="container">
      <div className="section__header">
        <p className="section__eyebrow">Deployment Options</p>
        <h2 className="section__title">Four Ways to Deploy</h2>
        <p className="section__sub">Choose the model that fits today. Upgrade when you're ready.</p>
      </div>
      <div className="how__grid">
        {deployments.map(d => (
          <div key={d.num} className="how-card">
            <div className="how-card__num">{d.num}</div>
            <div className="how-card__icon">{d.icon}</div>
            <h3 className="how-card__title">{d.title}</h3>
            <p className="how-card__desc">{d.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const industries = [
  { name: 'Healthcare', useCases: ['Appointment scheduling', 'Patient verification', 'Prescription status', 'Nurse triage', 'Care coordination'] },
  { name: 'Retail', useCases: ['Order status', 'Returns & refunds', 'Loyalty programs', 'Promotions', 'Store locator'] },
  { name: 'Banking', useCases: ['Balance inquiry', 'Fraud alerts', 'Card activation', 'Loan servicing'] },
  { name: 'Insurance', useCases: ['Claims intake (FNOL)', 'Policy lookup', 'Agent routing', 'Premium payment'] },
  { name: 'Travel', useCases: ['Reservations', 'Flight updates', 'Hotel bookings', 'Trip changes'] },
  { name: 'Education', useCases: ['Admissions', 'Student help desk', 'Financial aid', 'Enrollment'] },
  { name: 'Government', useCases: ['Citizen services', 'Benefits inquiries', 'Permit status', 'Appointment booking'] },
  { name: 'Manufacturing', useCases: ['Dealer support', 'Parts ordering', 'Warranty claims', 'Technical support'] },
];

const Industries = () => {
  const [active, setActive] = useState(industries[0].name);
  const current = industries.find(i => i.name === active)!;
  return (
    <section className="section industries" id="industries">
      <div className="container">
        <div className="section__header">
          <p className="section__eyebrow">Industry Solutions</p>
          <h2 className="section__title">Built for Your Industry</h2>
          <p className="section__sub">Pre-configured use cases accelerate your time to value.</p>
        </div>
        <div className="industries__layout">
          <div className="industries__tabs">
            {industries.map(i => (
              <button
                key={i.name}
                className={`ind-tab ${active === i.name ? 'ind-tab--active' : ''}`}
                onClick={() => setActive(i.name)}
              >{i.name}</button>
            ))}
          </div>
          <div className="industries__panel">
            <h3 className="industries__panel-title">{current.name}</h3>
            <ul className="industries__panel-list">
              {current.useCases.map(u => (
                <li key={u}><CheckCircle size={16} />{u}</li>
              ))}
            </ul>
            <a href="#contact" className="btn btn--primary" style={{ marginTop: '1.5rem', display: 'inline-flex' }}>
              Learn More <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const awsServices = [
  'Amazon Connect', 'Amazon Lex', 'Amazon Bedrock', 'AWS Lambda',
  'Amazon API Gateway', 'Amazon DynamoDB', 'Amazon S3', 'Amazon SNS',
  'Amazon SQS', 'Amazon EventBridge', 'Amazon CloudWatch', 'Amazon Cognito',
];

const AwsPowered = () => (
  <section className="section aws-section">
    <div className="container">
      <div className="aws-section__inner">
        <div className="aws-section__copy">
          <p className="section__eyebrow">AWS Native</p>
          <h2 className="section__title" style={{ textAlign: 'left', maxWidth: '100%' }}>Built Entirely on AWS</h2>
          <p className="section__sub" style={{ textAlign: 'left', maxWidth: '100%' }}>
            Saral Contact is not a wrapper—it's a purpose-built managed platform
            using 12+ AWS services. You benefit from AWS's continuous innovation
            without managing a single server.
          </p>
          <a href="#contact" className="btn btn--primary" style={{ marginTop: '1.5rem' }}>
            Request Architecture Review <ArrowRight size={16} />
          </a>
        </div>
        <div className="aws-section__badges">
          {awsServices.map(s => (
            <span key={s} className="aws-badge">{s}</span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState<FormStatus>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      await fetch('https://k96a4to6lk.execute-api.us-east-1.amazonaws.com/default/requestConsultation', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(form),
        mode: 'no-cors',
      });
      setStatus('success');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="section contact" id="contact">
      <div className="container">
        <div className="contact__layout">
          <div className="contact__copy">
            <p className="section__eyebrow" style={{ textAlign: 'left' }}>Get Started</p>
            <h2 className="section__title" style={{ textAlign: 'left', maxWidth: '100%', fontSize: '2rem' }}>
              Ready to Transform Your Customer Experience?
            </h2>
            <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginTop: '1rem' }}>
              Talk to a Saral Contact specialist to find the right tier for your business.
              Free consultation, no commitment.
            </p>
            <div className="contact__trust">
              {['HIPAA Compliant', 'PCI Certified', 'SOC Audited', 'AWS Partner'].map(t => (
                <span key={t} className="contact__trust-badge"><CheckCircle size={14} />{t}</span>
              ))}
            </div>
          </div>
          {status === 'success' ? (
            <div className="contact__success">
              <CheckCircle size={48} />
              <h3>Request Received!</h3>
              <p>Thank you, {form.name || 'there'}! A Saral Contact specialist will reach out within 1 business day.</p>
              <button className="btn btn--primary" onClick={() => setStatus('idle')}>Submit Another</button>
            </div>
          ) : (
            <form className="contact__form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input name="name" placeholder="Your Name" value={form.name} onChange={handleChange} required disabled={status === 'submitting'} />
                <input name="email" type="email" placeholder="Work Email" value={form.email} onChange={handleChange} required disabled={status === 'submitting'} />
              </div>
              <input name="company" placeholder="Company Name" value={form.company} onChange={handleChange} disabled={status === 'submitting'} />
              <textarea name="message" placeholder="Tell us about your contact center needs..." rows={4} value={form.message} onChange={handleChange} disabled={status === 'submitting'} />
              {status === 'error' && (
                <p className="contact__error">Something went wrong. Please try again or email us directly.</p>
              )}
              <button type="submit" className="btn btn--primary" style={{ width: '100%', justifyContent: 'center' }} disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending…' : <><span>Request Free Consultation</span> <ArrowRight size={16} /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="footer">
    <div className="container footer__inner">
      <div className="footer__brand">
        <div className="nav__logo" style={{ marginBottom: '0.75rem' }}>
          <span className="nav__logo-mark">S</span>
          <span className="nav__logo-text">Saral<strong>Contact</strong></span>
        </div>
        <p>AI-powered CCaaS built on Amazon Connect and AWS. From solopreneurs to enterprise.</p>
      </div>
      <div className="footer__links">
        <div>
          <h4>Platform</h4>
          <a href="#features">Features</a>
          <a href="#solutions">Service Tiers</a>
          <a href="#industries">Industries</a>
        </div>
        <div>
          <h4>Company</h4>
          <a href="#contact">Contact Us</a>
          <a href="#home">About</a>
        </div>
        <div>
          <h4>Compliance</h4>
          <a href="#contact">HIPAA</a>
          <a href="#contact">PCI DSS</a>
          <a href="#contact">SOC 2</a>
        </div>
      </div>
    </div>
    <div className="footer__bottom">
      <p>© 2026 SaralContact.com · Built on AWS · All rights reserved.</p>
    </div>
  </footer>
);

export default function App() {
  return (
    <>
      <Nav />
      <Hero />
      <Channels />
      <Features />
      <Solutions />
      <HowItWorks />
      <Industries />
      <AwsPowered />
      <Contact />
      <Footer />
    </>
  );
}
